import React, { useState, useEffect } from 'react';

// Interfaces for type safety
export interface DownloadInfo {
  url: string;
  filename: string;
  format: string;
  timestamp: number;
}

interface ExportRecord {
  messageId: string;
  exports: DownloadInfo[];
}

interface QuartoExportLinksProps {
  messageId: string;
  className?: string;
}

// Component to display export links for a specific message
export function QuartoExportLinks({ messageId, className = '' }: QuartoExportLinksProps) {
  const [exports, setExports] = useState<DownloadInfo[]>([]);

  useEffect(() => {
    // Get exports from global state
    const allExports = ExportLinksManager.getExportsForMessage(messageId);
    setExports(allExports);

    // Subscribe to changes
    const handleExportsUpdated = (updatedMessageId: string, updatedExports: DownloadInfo[]) => {
      if (updatedMessageId === messageId) {
        setExports([...updatedExports]);
      }
    };

    ExportLinksManager.subscribe(handleExportsUpdated);

    return () => {
      ExportLinksManager.unsubscribe(handleExportsUpdated);
    };
  }, [messageId]);

  if (exports.length === 0) {
    return null;
  }

  // Get file type icon
  const getFormatIcon = (format: string) => {
    switch (format.toLowerCase()) {
      case 'pdf':
        return (
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
        );
      case 'html':
        return (
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'docx':
      default:
        return (
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Format timestamp
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`quarto-export-links ${className}`}>
      <div className="flex flex-wrap gap-2">
        {exports.map((exportItem, index) => (
          <a
            key={`${messageId}-export-${index}`}
            href={exportItem.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-2 py-1 text-xs bg-muted hover:bg-accent text-foreground rounded transition-colors"
          >
            {getFormatIcon(exportItem.format)}
            <span>{exportItem.format.toUpperCase()}</span>
            <span className="text-muted-foreground text-xs">({formatTime(exportItem.timestamp)})</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// Singleton manager to track exports across the application
export class ExportLinksManager {
  private static exports: ExportRecord[] = [];
  private static listeners: ((messageId: string, exports: DownloadInfo[]) => void)[] = [];

  // Local storage key
  private static STORAGE_KEY = 'quarto_exports';

  // Initialize from local storage
  static initialize() {
    try {
      const storedExports = localStorage.getItem(this.STORAGE_KEY);
      if (storedExports) {
        this.exports = JSON.parse(storedExports);
      }
    } catch (e) {
      console.error('Failed to load exports from local storage:', e);
    }
  }

  // Save to local storage
  private static saveToStorage() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.exports));
    } catch (e) {
      console.error('Failed to save exports to local storage:', e);
    }
  }

  // Add a new export
  static addExport(messageId: string, downloadInfo: DownloadInfo) {
    const messageIndex = this.exports.findIndex(record => record.messageId === messageId);
    
    if (messageIndex >= 0) {
      // Add to existing message record
      this.exports[messageIndex].exports.push(downloadInfo);
    } else {
      // Create new message record
      this.exports.push({
        messageId,
        exports: [downloadInfo]
      });
    }
    
    // Save changes
    this.saveToStorage();
    
    // Notify listeners
    this.notifyListeners(messageId);
  }

  // Get exports for a specific message
  static getExportsForMessage(messageId: string): DownloadInfo[] {
    const record = this.exports.find(record => record.messageId === messageId);
    return record ? [...record.exports] : [];
  }

  // Subscribe to changes
  static subscribe(listener: (messageId: string, exports: DownloadInfo[]) => void) {
    this.listeners.push(listener);
  }

  // Unsubscribe from changes
  static unsubscribe(listener: (messageId: string, exports: DownloadInfo[]) => void) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  // Notify listeners of changes
  private static notifyListeners(messageId: string) {
    const exports = this.getExportsForMessage(messageId);
    this.listeners.forEach(listener => listener(messageId, exports));
  }

  // Clear all exports (mainly for testing)
  static clearAll() {
    this.exports = [];
    this.saveToStorage();
    
    // Notify all message IDs that were cleared
    const messageIds = this.exports.map(record => record.messageId);
    messageIds.forEach(id => this.notifyListeners(id));
  }
}

// Initialize on load
if (typeof window !== 'undefined') {
  ExportLinksManager.initialize();
}

export default QuartoExportLinks;